import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Heart, Users, Award } from 'lucide-react';

// AJ's actual images
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_fit-life-coach/artifacts/nsntflfl_IMG_7899.jpeg";
const PORTRAIT_IMAGE = "https://customer-assets.emergentagent.com/job_fit-life-coach/artifacts/z5x3jaiu_6957FE8B-9790-4174-A272-97B5877E41F2.png";
const GROUP_IMAGE = "https://customer-assets.emergentagent.com/job_a68a521f-d588-4c7a-99ce-4eb4c9c2d03b/artifacts/s50xz4s0_c681515e-c9a8-48a3-8db6-282e392fd3df.jpeg";

const About = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, [setIsVisible]);

  const values = [
    {
      icon: Heart,
      title: 'Real Understanding',
      description: 'As a first-time dad, I know how challenging it can be to prioritise fitness. Training should work with your life.'
    },
    {
      icon: Users,
      title: 'Experience Matters',
      description: 'Years of coaching beginners, busy professionals, parents, and runners has taught me what actually works.'
    },
    {
      icon: Award,
      title: 'No Gimmicks',
      description: 'Just honest, effective coaching. No extreme diets, no unrealistic promises — just sustainable results.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] flex items-end">
        <div className="absolute inset-0 z-0">
          <img 
            src={HERO_IMAGE}
            alt="AJ Javier - Head Coach"
            className="w-full h-full object-cover object-top md:object-[center_33%]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        </div>
        <div className={`container relative z-10 pb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h1 className="heading-display text-white">About</h1>
        </div>
      </section>

      {/* Intro Section */}
      <section className="bg-[#F5F0EB] py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="heading-large mb-6">Meet AJ Javier</h2>
              <h3 className="text-lg font-serif italic text-[#C9A962] mb-6">Head Coach</h3>
              <p className="body-large mb-6">
                I'm AJ, the founder and Head Coach of Heft Training. Based in East London, 
                I've spent years helping people just like you build strength, confidence, 
                and fitness habits that actually stick.
              </p>
              <p className="body-regular text-[#6B6B6B] mb-6">
                I'm not a fitness influencer. I'm a coach who believes training should 
                enhance your life, not dominate it. Whether you're a complete beginner 
                or looking to push past a plateau, my job is to meet you where you are 
                and help you get where you want to be.
              </p>
              <Link to="/contact" className="btn-gold inline-flex items-center gap-2">
                Work With Me
                <ArrowRight size={16} />
              </Link>
            </div>
            <div className="relative">
              <img 
                src={PORTRAIT_IMAGE}
                alt="AJ Javier - Head Coach"
                className="w-full aspect-[4/5] object-cover object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-large mb-4">What I Bring to Coaching</h2>
            <p className="body-large max-w-2xl mx-auto">
              Every client is different, but my approach stays consistent: 
              realistic, supportive, and focused on what works long-term.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {values.map((value) => (
              <div key={value.title} className="text-center p-8 border border-[#E5E0DB]">
                <div className="w-16 h-16 bg-[#C9A962] flex items-center justify-center mx-auto mb-6">
                  <value.icon size={28} className="text-[#1A1A1A]" />
                </div>
                <h3 className="heading-small mb-3">{value.title}</h3>
                <p className="body-regular text-[#6B6B6B]">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* My Approach Section */}
      <section className="bg-[#EDE8E3] py-20 md:py-28">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="heading-large mb-8 text-center">My Approach</h2>
            
            <div className="space-y-6 body-regular text-[#2D2D2D]">
              <p>
                When I started coaching, I quickly learned that the best training programme 
                in the world means nothing if it doesn't fit into someone's actual life. 
                People have jobs, families, social commitments — and training needs to work 
                around all of that.
              </p>
              
              <p>
                That's why I focus on consistency over perfection. I'd rather see you train 
                twice a week for a year than five times a week for three weeks before burning out. 
                Sustainable progress is the only kind that matters.
              </p>

              <p>
                My training philosophy centres on building strength. When you get stronger, 
                everything else follows — you move better, feel more confident, and your body 
                composition improves naturally. It's not about quick fixes or extreme measures; 
                it's about building a foundation that lasts.
              </p>

              <p>
                Becoming a first-time dad recently has only reinforced this belief. I understand 
                what it's like to have limited time and energy, and I know how important it is 
                to make training count without letting it take over your life.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who I Work With */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative">
              <img 
                src={GROUP_IMAGE}
                alt="Heft Training clients at HYROX"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
            <div>
              <h2 className="heading-large mb-8">Who I Work With</h2>
              <p className="body-large mb-8">
                Over the years, I've had the privilege of coaching a wide range of clients:
              </p>
              <ul className="space-y-4">
                {[
                  'Complete beginners taking their first steps into fitness',
                  'Busy professionals who need efficient, effective training',
                  'Parents juggling family life with self-care',
                  'Runners looking to build strength and prevent injuries',
                  "Anyone who's tried gyms before and wants something different"
                ].map((item) => (
                  <li key={item} className="flex gap-4">
                    <CheckCircle size={20} className="text-[#C9A962] flex-shrink-0 mt-0.5" />
                    <span className="body-regular">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-8">
                <Link to="/services" className="btn-outline">
                  View Coaching Services
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1A1A1A] py-20 md:py-28">
        <div className="container text-center">
          <h2 className="heading-large text-white mb-6">Let's Work Together</h2>
          <p className="body-large text-gray-400 mb-10 max-w-xl mx-auto">
            Ready to start building strength and confidence? Book a free consultation 
            to discuss your goals and see if we're a good fit.
          </p>
          <Link to="/contact" className="btn-gold inline-flex items-center gap-2">
            Book Free Consultation
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default About;
